'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useOptionalProduct } from '@/contexts/ProductContext';
import { useYotpoRefresh } from '@/lib/hooks/useYotpoRefresh';
import { lockBodyScroll } from '@/utils/scrollLock';

const PRODUCT_REVIEWS_INSTANCE_ID = '817757';
const STORE_BASE_URL = 'https://birchliving.com';
const REVIEWS_ANCHOR = 'yotpo_product_reviews';
const FILTERS_POPUP_ID = 'yotpo-mobile-filters-popup';

const YotpoProductReviewsClient = () => {
  const product = useOptionalProduct();
  const productData = product?.productData;
  const shopifyProductId = productData?.shopify_product_id;
  const shopifyProduct = productData?.shopifyProduct;
  const mounted = useSyncExternalStore(() => () => {}, () => true, () => false);
  const shouldRenderWidget = mounted && Boolean(shopifyProductId);
  const widgetRef = useRef(null);

  useEffect(() => {
    if (!mounted) return;
    if (window.location.hash !== `#${REVIEWS_ANCHOR}`) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 20;

    const tryScroll = () => {
      const el = document.getElementById(REVIEWS_ANCHOR);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      if (attempts++ < MAX_ATTEMPTS) setTimeout(tryScroll, 150);
    };

    tryScroll();
  }, [mounted]);

  useYotpoRefresh(shouldRenderWidget);

  // BL-942: Yotpo's filters drawer exposes no open/close event, so a
  // MutationObserver locks scroll while it's mounted. Scoped to this
  // widget's own container, where Yotpo mounts the drawer.
  useEffect(() => {
    const root = widgetRef.current;
    if (!shouldRenderWidget || !root) return;

    let unlock = null;
    const isFiltersPopup = (node) =>
      node instanceof Element && node.id === FILTERS_POPUP_ID;

    if (root.querySelector(`#${FILTERS_POPUP_ID}`)) unlock = lockBodyScroll();

    // BL-947: set once. A later filter can remove every .yotpo-review; the
    // server-rendered block must stay hidden.
    const markReviewsPainted = () => {
      if (!root.querySelector('.yotpo-review')) return;
      root.closest('[data-name="yotpo_product_reviews"]')
        ?.setAttribute('data-reviews-painted', '');
    };

    markReviewsPainted();

    const observer = new MutationObserver((mutations) => {
      markReviewsPainted();
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (isFiltersPopup(node)) unlock ??= lockBodyScroll();
        }
        for (const node of mutation.removedNodes) {
          if (isFiltersPopup(node)) {
            unlock?.();
            unlock = null;
          }
        }
      }
    });

    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      unlock?.();
    };
  }, [shouldRenderWidget]);

  if (!mounted) return <div className="min-h-[400px]" />;
  if (!shopifyProductId) return null;

  const productUrl = productData?.url
    ? `${STORE_BASE_URL}${productData.url}`
    : undefined;
  const priceAmount =
    shopifyProduct?.priceRange?.minVariantPrice?.amount ?? undefined;
  const priceCurrency =
    shopifyProduct?.priceRange?.minVariantPrice?.currencyCode ?? undefined;

  return (
    <div
      ref={widgetRef}
      className="yotpo-widget-instance"
      data-yotpo-instance-id={PRODUCT_REVIEWS_INSTANCE_ID}
      data-yotpo-product-id={shopifyProductId}
      data-yotpo-name={shopifyProduct?.title}
      data-yotpo-url={productUrl}
      data-yotpo-image-url={shopifyProduct?.featuredImage?.url}
      data-yotpo-price={priceAmount}
      data-yotpo-currency={priceCurrency}
    />
  );
};

export default YotpoProductReviewsClient;
